import {
    EncodedPacket,
    EncodedVideoPacketSource,
    MediaStreamAudioTrackSource,
    Mp4OutputFormat,
    Output,
    StreamTarget,
} from 'mediabunny';
import { createSoloVideoRecordingStream } from './createSoloVideoRecordingStream';

/**
 * SoloSingleFileVideoRecording - Records video+audio directly to a single MP4 file.
 *
 * Key features:
 * - Uses native MediaRecorder as a legacy fallback
 * - Uses a controllable WebCodecs + Mediabunny path when an official start boundary is provided
 * - File is complete and ready for playback immediately when recording stops
 * - MP4 format with H.264 codec for universal compatibility
 */

// Recording options
export interface SoloSingleFileVideoRecordingOptions {
    trackId: string;
    inputStream: MediaStream;       // borrowed capture stream; selected tracks are cloned for recorder ownership
    audioStream?: MediaStream;      // optional borrowed pre-processed audio stream; its selected track is cloned
    disableAudio?: boolean;         // record video without audio (default: false)
    videoBitrate?: number;          // video bitrate in bps (default: 4_000_000)
    audioBitrate?: number;          // audio bitrate in bps (default: 128_000)
    officialStartAtPerformanceMs?: number; // optional absolute countdown-zero boundary in performance.now() time
}

type StopResult = {
    trackId: string;
    filePath: string;
    file: File | null;
};

// Recording state
type RecordingState = 'idle' | 'warming' | 'recording' | 'stopping' | 'stopped';

const DEFAULT_VIDEO_BITRATE = 4_000_000; // 4 Mbps
const DEFAULT_AUDIO_BITRATE = 128_000;   // 128 kbps
const RECORDING_TIMESLICE_MS = 10_000;
const MODERN_PREPARE_TIMEOUT_MS = 10_000;
const MAX_VIDEO_ENCODER_QUEUE_SIZE = 4;
const DEFAULT_FRAME_RATE = 30;
const SOLO_VIDEO_KEYFRAME_INTERVAL_US = 2_000_000;
const MIN_VIDEO_BITRATE_BPS = 500_000;
const MAX_VIDEO_BITRATE_BPS = 60_000_000;
const AVC_CODEC_CANDIDATES = [
    'avc1.640034',
    'avc1.4d0034',
    'avc1.640028',
    'avc1.640C32',
    'avc1.64001f',
    'avc1.42001E',
];
const HARDWARE_ACCELERATION_PREFERENCES: Array<VideoEncoderConfig['hardwareAcceleration'] | undefined> = [
    'prefer-hardware',
    'prefer-software',
    undefined,
];
const AVC_ENCODER_PREWARM_TIMEOUT_MS = 5_000;
const avcEncoderConfigCache = new Map<string, Promise<VideoEncoderConfig>>();
const avcEncoderPrewarmCache = new Map<string, Promise<void>>();

// Preferred MIME types in order of preference (MP4 with H.264 for universal compatibility)
const PREFERRED_MIME_TYPES = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',  // H.264 Baseline + AAC
    'video/mp4;codecs=avc1.4D401E,mp4a.40.2',  // H.264 Main + AAC
    'video/mp4;codecs=avc1.64001E,mp4a.40.2',  // H.264 High + AAC
    'video/mp4;codecs=h264,aac',
    'video/mp4;codecs=avc1,mp4a',
    'video/mp4',
];

/**
 * Get the best supported MIME type for MediaRecorder
 */
function getSupportedMimeType(): string {
    for (const mimeType of PREFERRED_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
            return mimeType;
        }
    }
    // Fallback - let browser decide
    return '';
}

// OPFS path for solo recordings
const soloVideoRecordingPath = (trackId: string): string =>
    `solo-recordings/${trackId}.mp4`;

/**
 * Get the OPFS root directory.
 */
async function getOpfsRoot(): Promise<FileSystemDirectoryHandle> {
    return await navigator.storage.getDirectory();
}

/**
 * Create a file in OPFS and return a writable stream.
 */
async function createOpfsWritableFile(path: string): Promise<{
    fileHandle: FileSystemFileHandle;
    writable: FileSystemWritableFileStream;
}> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: true });
    }

    const fileHandle = await dir.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();

    return { fileHandle, writable };
}

/**
 * Read a file from OPFS and ensure it has the correct MIME type.
 */
async function readOpfsFile(path: string): Promise<File> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: false });
    }

    const fileHandle = await dir.getFileHandle(fileName, { create: false });
    const file = await fileHandle.getFile();

    // OPFS files may not have correct MIME type - ensure it's set for MP4
    if (!file.type || file.type === 'application/octet-stream') {
        const blob = await file.arrayBuffer();
        return new File([blob], fileName, { type: 'video/mp4' });
    }

    return file;
}

/**
 * Delete a file from OPFS.
 */
async function deleteOpfsFile(path: string): Promise<void> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        try {
            dir = await dir.getDirectoryHandle(segment, { create: false });
        } catch {
            return;
        }
    }

    try {
        await dir.removeEntry(fileName);
    } catch {
        // File doesn't exist
    }
}

function safeGetTrackSettings(track: MediaStreamTrack | null | undefined): MediaTrackSettings {
    try {
        if (typeof track?.getSettings === 'function') {
            return track.getSettings();
        }
    } catch { }
    return {};
}

function toEvenInt(value: number): number {
    const n = Math.max(2, Math.floor(value));
    return n % 2 === 0 ? n : n - 1;
}

function clampInt(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, Math.round(value)));
}

function buildCandidateBitrates(targetBitrateBps: number): number[] {
    const unique: number[] = [];
    for (const factor of [1, 0.85, 0.7, 0.55, 0.4]) {
        const candidate = clampInt(targetBitrateBps * factor, MIN_VIDEO_BITRATE_BPS, MAX_VIDEO_BITRATE_BPS);
        if (!unique.includes(candidate)) {
            unique.push(candidate);
        }
    }
    return unique;
}

async function pickSupportedAvcEncoderConfig(opts: {
    width: number;
    height: number;
    frameRate: number;
    targetBitrateBps: number;
}): Promise<VideoEncoderConfig> {
    if (typeof VideoEncoder === 'undefined') {
        throw new Error('VideoEncoder is not supported by this browser.');
    }

    const width = toEvenInt(opts.width);
    const height = toEvenInt(opts.height);
    const frameRate = Math.max(1, Math.round(opts.frameRate || DEFAULT_FRAME_RATE));
    const cacheKey = `${width}x${height}@${frameRate}:${Math.round(opts.targetBitrateBps)}`;
    const cachedConfig = avcEncoderConfigCache.get(cacheKey);
    if (cachedConfig) {
        return await cachedConfig;
    }

    const configPromise = (async () => {
        for (const bitrate of buildCandidateBitrates(opts.targetBitrateBps)) {
            for (const codec of AVC_CODEC_CANDIDATES) {
                for (const hardwareAcceleration of HARDWARE_ACCELERATION_PREFERENCES) {
                    const config: VideoEncoderConfig = {
                        codec,
                        width,
                        height,
                        bitrate,
                        framerate: frameRate,
                        bitrateMode: 'variable',
                        latencyMode: 'realtime',
                        hardwareAcceleration,
                    };

                    try {
                        const support = await VideoEncoder.isConfigSupported(config);
                        if (support.supported) {
                            return support.config ?? config;
                        }
                    } catch { }
                }
            }
        }

        throw new Error('No supported H.264 VideoEncoder config found for this camera stream.');
    })();

    avcEncoderConfigCache.set(cacheKey, configPromise);
    try {
        return await configPromise;
    } catch (error) {
        if (avcEncoderConfigCache.get(cacheKey) === configPromise) {
            avcEncoderConfigCache.delete(cacheKey);
        }
        throw error;
    }
}

async function prewarmAvcEncoder(config: VideoEncoderConfig): Promise<void> {
    const cacheKey = [
        config.codec,
        `${config.width}x${config.height}`,
        config.framerate,
        config.bitrate,
        config.hardwareAcceleration,
    ].join(':');
    const cachedPrewarm = avcEncoderPrewarmCache.get(cacheKey);
    if (cachedPrewarm) {
        return await cachedPrewarm;
    }

    const prewarmPromise = (async () => {
        const startedAt = performance.now();
        let encoder: VideoEncoder | null = null;
        let timeoutId: number | null = null;

        try {
            let rejectEncoderError: ((error: Error) => void) | null = null;
            const encoderErrorPromise = new Promise<never>((_resolve, reject) => {
                rejectEncoderError = reject;
            });

            encoder = new VideoEncoder({
                output: () => { },
                error: (error) => rejectEncoderError?.(error),
            });
            encoder.configure(config);

            const width = toEvenInt(config.width);
            const height = toEvenInt(config.height);
            const canvas = typeof OffscreenCanvas !== 'undefined'
                ? new OffscreenCanvas(width, height)
                : document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d') as
                | CanvasRenderingContext2D
                | OffscreenCanvasRenderingContext2D
                | null;
            if (context) {
                context.fillStyle = '#000';
                context.fillRect(0, 0, width, height);
            }

            const frame = new VideoFrame(canvas, {
                timestamp: 0,
                duration: Math.round(1_000_000 / Math.max(1, config.framerate ?? DEFAULT_FRAME_RATE)),
            });
            try {
                encoder.encode(frame, { keyFrame: true });
            } finally {
                frame.close();
            }

            const timeoutPromise = new Promise<never>((_resolve, reject) => {
                timeoutId = window.setTimeout(() => {
                    reject(new Error('Timed out while prewarming the H.264 encoder.'));
                }, AVC_ENCODER_PREWARM_TIMEOUT_MS);
            });
            await Promise.race([encoder.flush(), encoderErrorPromise, timeoutPromise]);

            console.log('[SoloSingleFileVideoRecording] H.264 encoder prewarmed', {
                durationMs: Math.round(performance.now() - startedAt),
                codec: config.codec,
                width,
                height,
                hardwareAcceleration: config.hardwareAcceleration,
            });
        } finally {
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
            try {
                if (encoder?.state !== 'closed') {
                    encoder?.close();
                }
            } catch { }
        }
    })();

    avcEncoderPrewarmCache.set(cacheKey, prewarmPromise);
    try {
        await prewarmPromise;
    } catch (error) {
        if (avcEncoderPrewarmCache.get(cacheKey) === prewarmPromise) {
            avcEncoderPrewarmCache.delete(cacheKey);
        }
        throw error;
    }
}

function getFrameTimestampUs(frame: VideoFrame, lastTimestampUs: number | null, fallbackFrameDurationUs: number): number {
    const rawTimestamp = (frame as any)?.timestamp;
    if (typeof rawTimestamp === 'number' && Number.isFinite(rawTimestamp)) {
        return rawTimestamp;
    }
    return (lastTimestampUs ?? 0) + fallbackFrameDurationUs;
}

function getFrameDurationUs(frame: VideoFrame, fallbackFrameDurationUs: number): number {
    const rawDuration = (frame as any)?.duration;
    if (typeof rawDuration === 'number' && Number.isFinite(rawDuration) && rawDuration > 0) {
        return rawDuration;
    }
    return fallbackFrameDurationUs;
}

function cloneVideoFrameWithTimestamp(frame: VideoFrame, timestampUs: number, durationUs: number): VideoFrame {
    const init: VideoFrameInit = {
        timestamp: Math.max(0, Math.round(timestampUs)),
    };
    if (Number.isFinite(durationUs) && durationUs > 0) {
        init.duration = Math.max(0, Math.round(durationUs));
    }
    return new VideoFrame(frame, init);
}

export class SoloSingleFileVideoRecording {
    private trackId: string;
    private providedAudioStream?: MediaStream;
    private inputStream: MediaStream;
    private disableAudio: boolean;
    private videoBitrate: number;
    private audioBitrate: number;
    private officialStartAtPerformanceMs: number | null;

    private state: RecordingState = 'idle';
    private stream: MediaStream | null = null;
    private ownsVideoTracks = false;
    private ownsAudioTracks = false;
    private mediaRecorder: MediaRecorder | null = null;
    private writable: FileSystemWritableFileStream | null = null;
    private opfsPath: string;
    private chunkCount = 0;
    private startTime = 0;
    private pendingWrite: Promise<void> = Promise.resolve();
    private startPromise: Promise<void> | null = null;
    private startCancellationRequested = false;

    private useModernPath = false;
    private modernOutput: Output | null = null;
    private modernVideoPacketSource: EncodedVideoPacketSource | null = null;
    private modernAudioSource: MediaStreamAudioTrackSource | null = null;
    private modernVideoEncoder: VideoEncoder | null = null;
    private modernVideoReader: ReadableStreamDefaultReader<VideoFrame> | null = null;
    private modernVideoLoopPromise: Promise<void> | null = null;
    private modernReadyPromise: Promise<void> | null = null;
    private resolveModernReadyPromise: (() => void) | null = null;
    private rejectModernReadyPromise: ((error: Error) => void) | null = null;
    private modernPendingVideoAdd: Promise<void> = Promise.resolve();
    private modernBytesWritten = 0;
    private modernPaused = false;
    private modernFatalError: Error | null = null;
    private modernEstimatedFrameDurationUs = Math.round(1_000_000 / DEFAULT_FRAME_RATE);
    private modernConfiguredWidth: number | null = null;
    private modernConfiguredHeight: number | null = null;
    private lastVideoInputTimestampUs: number | null = null;
    private totalPausedVideoDurationUs = 0;
    private officialRecordingStarted = false;
    private firstOfficialVideoInputTimestampUs: number | null = null;
    private hasQueuedOfficialVideoFrame = false;
    private hasWrittenOfficialVideoPacket = false;
    private nextForcedKeyFrameTimestampUs = 0;
    private videoSequenceNumber = 0;
    private lastVideoEncoderMeta: EncodedVideoChunkMetadata | undefined;

    constructor(options: SoloSingleFileVideoRecordingOptions) {
        this.trackId = options.trackId;
        this.providedAudioStream = options.audioStream;
        this.inputStream = options.inputStream;
        this.disableAudio = options.disableAudio ?? false;
        this.videoBitrate = options.videoBitrate ?? DEFAULT_VIDEO_BITRATE;
        this.audioBitrate = options.audioBitrate ?? DEFAULT_AUDIO_BITRATE;
        this.officialStartAtPerformanceMs = Number.isFinite(options.officialStartAtPerformanceMs)
            ? options.officialStartAtPerformanceMs!
            : null;
        this.opfsPath = soloVideoRecordingPath(this.trackId);
    }

    public static supportsOfficialStartBoundary(): boolean {
        return typeof VideoEncoder !== 'undefined' && typeof MediaStreamTrackProcessor !== 'undefined';
    }

    /**
     * Pays the browser/GPU H.264 cold-start cost before the live preview is shown.
     * The resulting supported config is cached and reused by the real recorder.
     */
    public static async prewarmH264Encoder(videoTrack: MediaStreamTrack): Promise<void> {
        if (typeof VideoEncoder === 'undefined') {
            return;
        }

        const settings = safeGetTrackSettings(videoTrack);
        if (!settings.width || !settings.height) {
            throw new Error('Camera dimensions are unavailable for H.264 prewarm.');
        }

        const config = await pickSupportedAvcEncoderConfig({
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate ?? DEFAULT_FRAME_RATE,
            targetBitrateBps: DEFAULT_VIDEO_BITRATE,
        });
        await prewarmAvcEncoder(config);
    }

    /**
     * Start recording video and audio.
     */
    public start(): Promise<void> {
        if (this.state !== 'idle') {
            console.warn('[SoloSingleFileVideoRecording] Cannot start, already recording');
            return this.startPromise ?? Promise.resolve();
        }

        this.state = 'warming';
        this.startCancellationRequested = false;
        console.log('[SoloSingleFileVideoRecording] Starting recording to:', this.opfsPath);

        const startPromise = this.startInternal();
        this.startPromise = startPromise;

        return startPromise.finally(() => {
            if (this.startPromise === startPromise) {
                this.startPromise = null;
            }
        });
    }

    private async startInternal(): Promise<void> {
        try {
            await this.prepareStream();
            this.throwIfStartCancelled();

            const videoTrack = this.stream?.getVideoTracks?.()[0];
            const audioTrack = this.stream?.getAudioTracks?.()[0];
            console.log('[SoloSingleFileVideoRecording] Got media stream:', {
                video: videoTrack?.getSettings?.(),
                audio: audioTrack?.getSettings?.(),
            });

            this.useModernPath = Boolean(this.officialStartAtPerformanceMs)
                && SoloSingleFileVideoRecording.supportsOfficialStartBoundary();

            if (Boolean(this.officialStartAtPerformanceMs) && !this.useModernPath) {
                console.warn('[SoloSingleFileVideoRecording] Falling back to MediaRecorder because WebCodecs warm-up support is unavailable');
            }

            if (this.useModernPath) {
                await this.startModernRecording(videoTrack ?? null, audioTrack ?? null);
                return;
            }

            await this.startLegacyRecording();
        } catch (error) {
            await this.abortFailedStart();
            throw error;
        }
    }

    private throwIfStartCancelled(): void {
        if (this.startCancellationRequested) {
            throw new Error('Video recording startup was cancelled.');
        }
    }

    private async prepareStream(): Promise<void> {
        this.stream = createSoloVideoRecordingStream({
            inputStream: this.inputStream,
            audioStream: this.providedAudioStream,
            disableAudio: this.disableAudio,
        });
        this.ownsVideoTracks = true;
        this.ownsAudioTracks = this.stream.getAudioTracks().length > 0;

        console.log('[SoloSingleFileVideoRecording] Created dedicated recording track branch', {
            sourceVideoTrackId: this.inputStream.getVideoTracks()[0]?.id,
            recordingVideoTrackId: this.stream.getVideoTracks()[0]?.id,
            sourceAudioTrackId: (this.providedAudioStream ?? this.inputStream).getAudioTracks()[0]?.id,
            recordingAudioTrackId: this.stream.getAudioTracks()[0]?.id,
        });
    }

    private async startLegacyRecording(): Promise<void> {
        const mimeType = getSupportedMimeType();
        console.log('[SoloSingleFileVideoRecording] Using MIME type:', mimeType || 'browser default');

        const options: MediaRecorderOptions = {
            videoBitsPerSecond: this.videoBitrate,
            audioBitsPerSecond: this.audioBitrate,
        };
        if (mimeType) {
            options.mimeType = mimeType;
        }

        this.mediaRecorder = new MediaRecorder(this.stream as MediaStream, options);
        this.chunkCount = 0;

        const { writable } = await createOpfsWritableFile(this.opfsPath);
        this.writable = writable;
        this.throwIfStartCancelled();

        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size <= 0) {
                return;
            }

            this.chunkCount += 1;
            this.pendingWrite = this.pendingWrite.then(async () => {
                try {
                    await this.writable?.write(event.data);
                } catch (e) {
                    console.error('[SoloSingleFileVideoRecording] Error writing to OPFS:', e);
                }
            });

            console.log('[SoloSingleFileVideoRecording] Chunk recorded:', {
                size: event.data.size,
                totalChunks: this.chunkCount,
                isBackground: document.visibilityState === 'hidden',
            });
        };

        this.mediaRecorder.onerror = (event) => {
            console.error('[SoloSingleFileVideoRecording] MediaRecorder error:', event);
        };

        this.startTime = Date.now();
        this.mediaRecorder.start(RECORDING_TIMESLICE_MS);
        this.state = 'recording';

        console.log('[SoloSingleFileVideoRecording] Recording started', {
            trackId: this.trackId,
            videoBitrate: this.videoBitrate,
            audioBitrate: this.audioBitrate,
            mimeType: mimeType || 'default',
        });
    }

    private async startModernRecording(videoTrack: MediaStreamTrack | null, audioTrack: MediaStreamTrack | null): Promise<void> {
        if (!videoTrack) {
            throw new Error('No video track available for solo recording.');
        }

        this.modernReadyPromise = new Promise<void>((resolve, reject) => {
            this.resolveModernReadyPromise = resolve;
            this.rejectModernReadyPromise = reject;
        });
        // Cancellation can happen while OPFS or Mediabunny is still starting,
        // before the readiness promise reaches its await below.
        void this.modernReadyPromise.catch(() => { });

        const settings = safeGetTrackSettings(videoTrack);
        const frameRate = Math.max(1, Math.round(settings.frameRate ?? DEFAULT_FRAME_RATE));
        this.modernEstimatedFrameDurationUs = Math.round(1_000_000 / frameRate);

        const { writable } = await createOpfsWritableFile(this.opfsPath);
        this.writable = writable;
        this.throwIfStartCancelled();

        const target = new StreamTarget(writable as unknown as WritableStream<any>, { chunked: true });
        (target as any).onwrite = (_start: number, end: number) => {
            this.modernBytesWritten = Math.max(this.modernBytesWritten, end);
        };

        this.modernOutput = new Output({
            format: new Mp4OutputFormat(),
            target,
        });

        this.modernVideoPacketSource = new EncodedVideoPacketSource('avc');
        this.modernOutput.addVideoTrack(this.modernVideoPacketSource);

        if (audioTrack && !this.disableAudio) {
            this.modernAudioSource = new MediaStreamAudioTrackSource(audioTrack as any, {
                codec: 'aac',
                bitrate: this.audioBitrate,
                bitrateMode: 'variable',
            } as any);
            this.modernAudioSource.pause();
            this.modernOutput.addAudioTrack(this.modernAudioSource);
            void this.modernAudioSource.errorPromise.catch((error) => {
                this.handleModernFatalError(error);
            });
        }

        await this.modernOutput.start();
        this.throwIfStartCancelled();

        const processor = new MediaStreamTrackProcessor({ track: videoTrack as any });
        this.modernVideoReader = (processor.readable as unknown as ReadableStream<VideoFrame>).getReader();

        this.state = this.isWaitingForOfficialStart() ? 'warming' : 'recording';
        if (!this.isWaitingForOfficialStart()) {
            this.officialRecordingStarted = true;
            this.startTime = Date.now();
            this.modernAudioSource?.resume();
        }

        this.modernVideoLoopPromise = this.processModernVideoFrames().catch((error) => {
            this.handleModernFatalError(error);
        });

        console.log('[SoloSingleFileVideoRecording] Modern recording pipeline started', {
            trackId: this.trackId,
            videoBitrate: this.videoBitrate,
            audioBitrate: this.audioBitrate,
            officialStartAtPerformanceMs: this.officialStartAtPerformanceMs,
        });

        const prepareTimeout = window.setTimeout(() => {
            this.rejectModernReady(new Error('Timed out while preparing the video encoder.'));
        }, MODERN_PREPARE_TIMEOUT_MS);
        try {
            await this.modernReadyPromise;
        } finally {
            window.clearTimeout(prepareTimeout);
        }

        console.log('[SoloSingleFileVideoRecording] Modern recording pipeline ready');
    }

    private resolveModernReady(): void {
        if (!this.resolveModernReadyPromise) return;
        const resolve = this.resolveModernReadyPromise;
        this.resolveModernReadyPromise = null;
        this.rejectModernReadyPromise = null;
        resolve();
    }

    private rejectModernReady(error: Error): void {
        if (!this.rejectModernReadyPromise) return;
        const reject = this.rejectModernReadyPromise;
        this.resolveModernReadyPromise = null;
        this.rejectModernReadyPromise = null;
        reject(error);
    }

    private isWaitingForOfficialStart(): boolean {
        return Boolean(this.officialStartAtPerformanceMs)
            && performance.now() < (this.officialStartAtPerformanceMs as number);
    }

    private async processModernVideoFrames(): Promise<void> {
        const reader = this.modernVideoReader;
        if (!reader) {
            return;
        }

        while (this.state !== 'stopping' && this.state !== 'stopped') {
            const result = await reader.read();
            if (result.done) {
                break;
            }

            const frame = result.value;
            if (!frame) {
                continue;
            }

            try {
                await this.processModernVideoFrame(frame);
            } finally {
                frame.close();
            }
        }
    }

    private async processModernVideoFrame(frame: VideoFrame): Promise<void> {
        if (this.state === 'stopping' || this.state === 'stopped') {
            return;
        }

        const width = frame.codedWidth;
        const height = frame.codedHeight;
        if ((width % 2) !== 0 || (height % 2) !== 0) {
            throw new Error(`Solo recording does not support odd video dimensions (${width}x${height}) for H.264.`);
        }

        await this.ensureModernVideoEncoder(frame);

        if (this.modernConfiguredWidth !== width || this.modernConfiguredHeight !== height) {
            throw new Error(`Solo recording video dimensions changed from ${this.modernConfiguredWidth}x${this.modernConfiguredHeight} to ${width}x${height}.`);
        }

        const frameTimestampUs = getFrameTimestampUs(frame, this.lastVideoInputTimestampUs, this.modernEstimatedFrameDurationUs);
        const frameDurationUs = getFrameDurationUs(frame, this.modernEstimatedFrameDurationUs);

        if (!this.officialRecordingStarted && !this.isWaitingForOfficialStart()) {
            await this.promoteWarmupToOfficialRecording();
        }

        if (!this.officialRecordingStarted) {
            this.lastVideoInputTimestampUs = frameTimestampUs;
            this.encodeVideoFrame(frame, frameTimestampUs, frameDurationUs, false);
            return;
        }

        if (this.modernPaused) {
            if (this.lastVideoInputTimestampUs !== null) {
                const timeDelta = Math.max(0, frameTimestampUs - this.lastVideoInputTimestampUs);
                this.totalPausedVideoDurationUs += timeDelta;
            }
            this.lastVideoInputTimestampUs = frameTimestampUs;
            return;
        }

        if (this.firstOfficialVideoInputTimestampUs === null) {
            this.firstOfficialVideoInputTimestampUs = frameTimestampUs;
            this.startTime = Date.now();
        }

        const adjustedTimestampUs = Math.max(
            0,
            frameTimestampUs - this.firstOfficialVideoInputTimestampUs - this.totalPausedVideoDurationUs,
        );

        this.lastVideoInputTimestampUs = frameTimestampUs;

        const shouldForceKeyFrame = !this.hasQueuedOfficialVideoFrame
            || adjustedTimestampUs >= this.nextForcedKeyFrameTimestampUs;
        if (shouldForceKeyFrame) {
            this.nextForcedKeyFrameTimestampUs = adjustedTimestampUs + SOLO_VIDEO_KEYFRAME_INTERVAL_US;
        }

        this.hasQueuedOfficialVideoFrame = true;
        this.encodeVideoFrame(frame, adjustedTimestampUs, frameDurationUs, shouldForceKeyFrame);
    }

    private async ensureModernVideoEncoder(frame: VideoFrame): Promise<void> {
        if (this.modernVideoEncoder) {
            return;
        }

        const settings = safeGetTrackSettings(this.stream?.getVideoTracks?.()[0]);
        const frameRate = Math.max(1, Math.round(settings.frameRate ?? DEFAULT_FRAME_RATE));
        const configSelectionStartedAt = performance.now();
        const config = await pickSupportedAvcEncoderConfig({
            width: frame.codedWidth,
            height: frame.codedHeight,
            frameRate,
            targetBitrateBps: this.videoBitrate,
        });

        this.modernConfiguredWidth = config.width;
        this.modernConfiguredHeight = config.height;
        this.modernVideoEncoder = new VideoEncoder({
            output: (chunk, meta) => {
                this.handleModernVideoChunk(chunk, meta);
            },
            error: (error) => {
                this.handleModernFatalError(error);
            },
        });
        this.modernVideoEncoder.configure(config);

        console.log('[SoloSingleFileVideoRecording] Using VideoEncoderConfig', {
            ...config,
            selectionDurationMs: Math.round(performance.now() - configSelectionStartedAt),
        });
    }

    private encodeVideoFrame(frame: VideoFrame, timestampUs: number, durationUs: number, keyFrame: boolean): void {
        const encoder = this.modernVideoEncoder;
        if (!encoder) {
            return;
        }

        if (encoder.encodeQueueSize >= MAX_VIDEO_ENCODER_QUEUE_SIZE) {
            return;
        }

        const encodedFrame = cloneVideoFrameWithTimestamp(frame, timestampUs, durationUs);
        try {
            encoder.encode(encodedFrame, { keyFrame });
        } finally {
            encodedFrame.close();
        }
    }

    private async promoteWarmupToOfficialRecording(): Promise<void> {
        if (this.officialRecordingStarted) {
            return;
        }

        if (this.modernVideoEncoder) {
            await this.modernVideoEncoder.flush();
        }
        await this.modernPendingVideoAdd.catch(() => { });

        this.officialRecordingStarted = true;
        this.state = 'recording';
        this.totalPausedVideoDurationUs = 0;
        this.firstOfficialVideoInputTimestampUs = null;
        this.hasQueuedOfficialVideoFrame = false;
        this.hasWrittenOfficialVideoPacket = false;
        this.videoSequenceNumber = 0;
        this.nextForcedKeyFrameTimestampUs = 0;
        this.lastVideoInputTimestampUs = null;
        this.modernAudioSource?.resume();

        console.log('[SoloSingleFileVideoRecording] Official recording boundary reached');
    }

    private handleModernVideoChunk(chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void {
        this.resolveModernReady();

        if (meta?.decoderConfig) {
            this.lastVideoEncoderMeta = meta;
        }

        if (!this.officialRecordingStarted) {
            return;
        }

        const packet = EncodedPacket.fromEncodedChunk(chunk);
        if (!this.hasWrittenOfficialVideoPacket && packet.type !== 'key') {
            console.warn('[SoloSingleFileVideoRecording] Dropping non-key first official video packet');
            return;
        }

        const packetMeta = meta?.decoderConfig
            ? meta
            : (!this.hasWrittenOfficialVideoPacket ? this.lastVideoEncoderMeta : undefined);

        const sequencedPacket = packet.clone({
            sequenceNumber: this.videoSequenceNumber++,
        });

        this.modernPendingVideoAdd = this.modernPendingVideoAdd.then(async () => {
            await this.modernVideoPacketSource?.add(sequencedPacket, packetMeta);
            this.hasWrittenOfficialVideoPacket = true;
        }).catch((error) => {
            this.handleModernFatalError(error);
        });
    }

    private handleModernFatalError(error: unknown): void {
        const err = error instanceof Error ? error : new Error(String(error));
        this.modernFatalError = this.modernFatalError ?? err;
        this.rejectModernReady(err);
        console.error('[SoloSingleFileVideoRecording] Modern recorder error:', err);

        if (this.state !== 'stopping' && this.state !== 'stopped') {
            try {
                void this.modernVideoReader?.cancel();
            } catch { }
        }
    }

    private async abortFailedStart(): Promise<void> {
        this.state = 'stopping';

        try {
            await this.modernVideoReader?.cancel();
        } catch { }
        this.modernVideoReader = null;

        try {
            await this.modernVideoLoopPromise;
        } catch { }

        this.closeModernVideoEncoder();

        if (this.modernOutput) {
            try {
                await this.modernOutput.cancel();
            } catch { }
        } else if (this.writable) {
            try {
                await this.writable.abort();
            } catch { }
        }

        await this.cleanupOwnedMediaTracks();
        await deleteOpfsFile(this.opfsPath);

        this.mediaRecorder = null;
        this.writable = null;
        this.resetModernState();
        this.state = 'stopped';
    }

    /**
     * Stop recording and finalize the file.
     */
    public async stop(): Promise<StopResult> {
        if (this.startPromise) {
            this.startCancellationRequested = true;
            this.rejectModernReady(new Error('Video recording startup was cancelled.'));
            try {
                await this.startPromise;
            } catch { }

            if (!this.isRecording()) {
                return {
                    trackId: this.trackId,
                    filePath: this.opfsPath,
                    file: null,
                };
            }
        }

        if (this.state !== 'recording' && this.state !== 'warming') {
            console.warn('[SoloSingleFileVideoRecording] Cannot stop, not recording');
            throw new Error('Not recording');
        }

        if (this.useModernPath) {
            return await this.stopModernRecording();
        }

        return await this.stopLegacyRecording();
    }

    private async stopLegacyRecording(): Promise<StopResult> {
        this.state = 'stopping';
        const recordingDuration = Date.now() - this.startTime;
        console.log('[SoloSingleFileVideoRecording] Stopping recording...', {
            duration: recordingDuration,
            chunks: this.chunkCount,
        });

        await new Promise<void>((resolve) => {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
                resolve();
                return;
            }

            this.mediaRecorder.onstop = () => {
                console.log('[SoloSingleFileVideoRecording] MediaRecorder stopped');
                resolve();
            };

            try {
                this.mediaRecorder.requestData();
            } catch (err) {
                console.warn('[SoloSingleFileVideoRecording] MediaRecorder.requestData() failed:', err);
            }
            this.mediaRecorder.stop();
        });

        await this.cleanupOwnedMediaTracks();

        console.log('[SoloSingleFileVideoRecording] Waiting for pending writes...');
        const waitStart = Date.now();
        const maxWaitMs = 5_000;
        let lastChunkCount = this.chunkCount;
        try {
            while (true) {
                await this.pendingWrite;
                await new Promise<void>((r) => setTimeout(r, 0));
                if (this.chunkCount === lastChunkCount) {
                    break;
                }
                lastChunkCount = this.chunkCount;
                if (Date.now() - waitStart > maxWaitMs) {
                    console.warn('[SoloSingleFileVideoRecording] Timed out waiting for final chunk writes', {
                        lastChunkCount,
                        currentChunkCount: this.chunkCount,
                    });
                    break;
                }
            }
        } catch (e) {
            console.error('[SoloSingleFileVideoRecording] Error in pending writes:', e);
        }

        if (this.writable) {
            try {
                await this.writable.close();
            } catch (e) {
                console.error('[SoloSingleFileVideoRecording] Error closing writable:', e);
            }
            this.writable = null;
        }

        this.state = 'stopped';
        this.mediaRecorder = null;

        const file = await readOpfsFile(this.opfsPath);

        console.log('[SoloSingleFileVideoRecording] Recording complete!', {
            fileSize: file.size,
            fileType: file.type,
            fileName: file.name,
            duration: recordingDuration,
            path: this.opfsPath,
        });

        return {
            trackId: this.trackId,
            filePath: this.opfsPath,
            file,
        };
    }

    private async stopModernRecording(): Promise<StopResult> {
        this.state = 'stopping';
        this.rejectModernReady(new Error('Video preparation was cancelled.'));

        try {
            try {
                await this.modernVideoReader?.cancel();
            } catch { }
            this.modernVideoReader = null;

            try {
                await this.modernVideoLoopPromise;
            } catch { }

            if (this.modernVideoEncoder) {
                try {
                    await this.modernVideoEncoder.flush();
                } catch (error) {
                    this.handleModernFatalError(error);
                }
            }

            await this.modernPendingVideoAdd.catch(() => { });

            if (!this.officialRecordingStarted) {
                try {
                    await this.modernOutput?.cancel();
                } catch (error) {
                    console.warn('[SoloSingleFileVideoRecording] Failed to cancel warm-up output:', error);
                }
                await this.cleanupOwnedMediaTracks();
                await deleteOpfsFile(this.opfsPath);
                this.state = 'stopped';
                this.resetModernState();
                return {
                    trackId: this.trackId,
                    filePath: this.opfsPath,
                    file: null,
                };
            }

            try {
                this.modernVideoPacketSource?.close();
            } catch { }
            try {
                this.modernAudioSource?.close();
            } catch { }

            try {
                await this.modernOutput?.finalize();
            } catch (error) {
                console.warn('[SoloSingleFileVideoRecording] Error finalizing MP4 output:', error);
                this.handleModernFatalError(error);
            }

            await this.cleanupOwnedMediaTracks();

            const recordingDuration = this.startTime > 0 ? Date.now() - this.startTime : 0;

            if (this.modernBytesWritten <= 0) {
                await deleteOpfsFile(this.opfsPath);
                this.state = 'stopped';
                this.resetModernState();
                return {
                    trackId: this.trackId,
                    filePath: this.opfsPath,
                    file: null,
                };
            }

            const file = await readOpfsFile(this.opfsPath);

            console.log('[SoloSingleFileVideoRecording] Recording complete!', {
                fileSize: file.size,
                fileType: file.type,
                fileName: file.name,
                duration: recordingDuration,
                path: this.opfsPath,
                officialRecordingStarted: this.officialRecordingStarted,
                hadFatalError: Boolean(this.modernFatalError),
            });

            this.state = 'stopped';
            this.resetModernState();

            return {
                trackId: this.trackId,
                filePath: this.opfsPath,
                file,
            };
        } finally {
            this.closeModernVideoEncoder();
        }
    }

    private closeModernVideoEncoder(): void {
        if (!this.modernVideoEncoder) {
            return;
        }
        try {
            if (this.modernVideoEncoder.state !== 'closed') {
                this.modernVideoEncoder.close();
            }
        } catch { }
        this.modernVideoEncoder = null;
    }

    private resetModernState(): void {
        this.modernOutput = null;
        this.modernVideoPacketSource = null;
        this.modernAudioSource = null;
        this.modernVideoReader = null;
        this.modernVideoLoopPromise = null;
        this.modernReadyPromise = null;
        this.resolveModernReadyPromise = null;
        this.rejectModernReadyPromise = null;
        this.modernPendingVideoAdd = Promise.resolve();
        this.modernBytesWritten = 0;
        this.modernPaused = false;
        this.modernConfiguredWidth = null;
        this.modernConfiguredHeight = null;
        this.lastVideoInputTimestampUs = null;
        this.totalPausedVideoDurationUs = 0;
        this.officialRecordingStarted = false;
        this.firstOfficialVideoInputTimestampUs = null;
        this.hasQueuedOfficialVideoFrame = false;
        this.hasWrittenOfficialVideoPacket = false;
        this.nextForcedKeyFrameTimestampUs = 0;
        this.videoSequenceNumber = 0;
        this.lastVideoEncoderMeta = undefined;
        this.writable = null;
    }

    private async cleanupOwnedMediaTracks(): Promise<void> {
        if (!this.stream) {
            return;
        }

        if (this.ownsVideoTracks) {
            this.stream.getVideoTracks().forEach((track) => track.stop());
        }
        if (this.ownsAudioTracks) {
            this.stream.getAudioTracks().forEach((track) => track.stop());
        }

        this.stream = null;
    }

    /**
     * Pause recording.
     */
    public async pause(): Promise<void> {
        if (this.state !== 'recording') {
            return;
        }

        if (this.useModernPath) {
            this.modernPaused = true;
            this.modernAudioSource?.pause();
            return;
        }

        const recorder = this.mediaRecorder;
        if (!recorder || recorder.state !== 'recording') {
            return;
        }
        try {
            recorder.pause();
        } catch (err) {
            console.warn('[SoloSingleFileVideoRecording] Failed to pause:', err);
        }
    }

    /**
     * Resume recording after a pause.
     */
    public async resume(): Promise<void> {
        if (this.state !== 'recording') {
            return;
        }

        if (this.useModernPath) {
            this.modernPaused = false;
            this.modernAudioSource?.resume();
            return;
        }

        const recorder = this.mediaRecorder;
        if (!recorder || recorder.state !== 'paused') {
            return;
        }
        try {
            recorder.resume();
        } catch (err) {
            console.warn('[SoloSingleFileVideoRecording] Failed to resume:', err);
        }
    }

    /**
     * Check if the underlying recorder is paused.
     */
    public isPaused(): boolean {
        if (this.useModernPath) {
            return this.modernPaused;
        }
        return this.mediaRecorder?.state === 'paused';
    }

    /**
     * Check if currently recording or warming up.
     */
    public isRecording(): boolean {
        return this.state === 'warming' || this.state === 'recording';
    }

    /**
     * Get the track ID.
     */
    public getTrackId(): string {
        return this.trackId;
    }

    /**
     * Get the OPFS path where the file will be saved.
     */
    public getFilePath(): string {
        return this.opfsPath;
    }

    /**
     * Clean up the recording file.
     */
    public async cleanup(): Promise<void> {
        this.startCancellationRequested = true;
        this.rejectModernReady(new Error('Video recording startup was cancelled.'));

        if (this.startPromise) {
            try {
                await this.startPromise;
            } catch { }
        }

        if (this.isRecording()) {
            try {
                await this.stop();
            } catch { }
        }

        await this.cleanupOwnedMediaTracks();
        await deleteOpfsFile(this.opfsPath);
        console.log('[SoloSingleFileVideoRecording] Cleaned up:', this.opfsPath);
    }
}

// Generate a unique track ID for solo recordings
export function generateSoloTrackId(): string {
    return `solo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Export utility functions
export { readOpfsFile as readVideoOpfsFile, deleteOpfsFile as deleteVideoOpfsFile, soloVideoRecordingPath };
